import { accountTypes, getAssignedToAccountTypes, InputType, loanOptionsConstants } from 'config'
import { states } from 'config/states.constants'
import { loanTypeMap } from 'reducers/loanDetail.reducer'
import { filterUserRoles } from 'services'

export const profileCompanyInfoKeys = [
  'companyStateOfFormation',
  'companyEIN',
  'companyName',
  'companyNmls',
  'otherLicenseID',
  'companyType',
  'companyAddress',
]

export const profileCompanyInfoVisibleKeys = ['companyEIN', 'companyName', 'companyNmls', 'otherLicenseID']

export const defaultInputs = (): Record<string, InputType> => {
  return {
    accountType: {
      title: 'Account Type',
      inputType: 'select',
      options: accountTypes,
      visible: true,
      hasDefaultOption: true,
      required: true,
      sort: true,
      sortKey: 'value',
    },
    accountExecutive: {
      title: 'Account Executive Associated With',
      inputType: 'SearchableSelect',
      options: async (query: string) => {
        const data = await filterUserRoles(`${query}`.trim())
        return data
      },
      hasDefaultOption: true,
      value: '0',
      sort: true,
      required: true,
    },
    broker: {
      title: 'Broker Associated With',
      inputType: 'select',
      options: [],
      hasDefaultOption: true,
      value: '0',
      sort: true,
      required: true,
    },
    branch: {
      title: 'Branch Associated With',
      inputType: 'select',
      options: [],
      required: false,
      hasDefaultOption: true,
      defaultOptionText: 'Main Office',
      allowDefaultOption: true,
      sort: true,
      value: '0',
    },
    branchAccountExecutive: {
      title: 'Branch Account Executive',
      inputType: 'select',
      options: [],
      hasDefaultOption: true,
      defaultOptionText: 'None',
      value: '0',
      sort: true,
      required: false,
    },
    companySeperator: {
      inputType: 'section',
      title: 'Company Information',
      span: 2,
    },
    companyStateOfFormation: {
      inputType: 'select',
      title: 'State of Formation',
      options: states,
      hasDefaultOption: true,
    },
    companyType: {
      inputType: 'select',
      options: loanOptionsConstants.entityType,
      hasDefaultOption: true,
      title: 'Company Type',
      required: true,
    },
    companyEIN: {
      inputType: 'text',
      type: 'text',
      title: 'Company EIN',
    },
    companyName: {
      inputType: 'text',
      title: 'Company Name',
      required: true,
    },
    companyNmls: {
      inputType: 'text',
      type: 'text',
      title: 'Company NMLS ID',
      required: false,
    },
    otherLicenseID: {
      inputType: 'text',
      type: 'text',
      title: 'Other License ID',
      required: false,
    },
    companyAddress: {
      inputType: 'map',
      title: 'Company Address',
      span: 2,
      allowForeign: true,
      requireValidate: false,
      required: true,
    },
    personalSeperator1: {
      inputType: 'section',
      title: 'Personal Information',
      span: 2,
    },
    personalSeperator2: {
      inputType: 'section',
      title: 'Personal Information',
      span: 2,
    },
    title: {
      inputType: 'text',
      title: 'Principal Title',
    },
    name: {
      inputType: 'text',
      title: 'Principal Full Name',
      required: true,
    },
    email: {
      inputType: 'text',
      type: 'email',
      title: 'Principal Email',
      required: true,
    },
    phone: {
      inputType: 'text',
      type: 'phone',
      title: 'Principal Phone Number',
      required: true,
    },
    phoneExt: {
      inputType: 'text',
      type: 'phone',
      title: 'Principal Phone Ext',
      required: false,
    },
    brokerSSN: {
      inputType: 'text',
      type: 'ssn',
      title: 'Broker Social Security Number',
      required: false,
    },
    brokerNmls: {
      inputType: 'text',
      title: 'Broker NMLS ID / Real Estate License ID',
      required: false,
      span: 2,
    },
    enableWhiteLabel: {
      inputType: 'toggle',
      title: 'White Label Approved',
      span: 2,
      value: false,
      required: true,
    },
    logo: {
      inputType: 'file',
      title: 'Logo',
      required: false,
      multiple: false,
      acceptFileTypes: 'image/*',
    },
    logoPreview: {
      inputType: 'custom',
      title: 'Logo Preview',
    },
    brokerFooter: {
      inputType: 'custom',
      title: 'Custom Footer',
    },
    branchNmls: {
      inputType: 'text',
      title: 'Branch NMLS ID',
      required: false,
    },
    companyLicense: {
      inputType: 'text',
      title: 'Company State license ID',
      required: false,
    },
    loanOfficer: {
      inputType: 'text',
      title: 'Loan Officer NMLS ID',
    },
    minCompensation: {
      inputType: 'text',
      type: 'number',
      title: 'Min Compensation',
      required: false,
      prefix: '%',
    },
    maxCompensation: {
      inputType: 'text',
      type: 'number',
      title: 'Max Compensation',
      required: false,
      prefix: '%',
    },
    brokerCompensation: {
      inputType: 'text',
      type: 'number',
      title: 'Broker/Branch Compensation',
      required: false,
      prefix: '%',
    },
    loanOfficerStateLicense: {
      inputType: 'text',
      title: 'Loan Officer State license ID',
      required: false,
    },
    taskSeperator: {
      inputType: 'section',
      title: 'Task Information',
      span: 2,
    },
    taskAssignViewTypes: {
      inputType: 'multiselect',
      title: 'Task Assign View',
      options: getAssignedToAccountTypes(),
      defaultSelected: false,
      value: {},
      span: 2,
    },
    pipelineSeperator: {
      inputType: 'section',
      title: 'Pipeline',
      span: 2,
    },
    pipelineViewTypes: {
      inputType: 'multiselect',
      title: 'Pipeline View',
      options: getAssignedToAccountTypes(),
      defaultSelected: false,
      value: {},
      span: 1,
    },
    defaultChannel: {
      inputType: 'select',
      options: loanTypeMap,
      title: 'Default Channel',
      defaultSelected: true,
      span: 1,
    },

    parentMarginSeperator1: {
      inputType: 'section',
      title: 'Parent Margin Adjustment',
      span: 2,
      visible: false,
    },
    parentMarginRate: {
      inputType: 'text',
      type: 'number',
      title: 'Rate Margin',
      required: false,
      visible: false,
    },
    parentMarginPrice: {
      inputType: 'text',
      type: 'number',
      title: 'Price Margin',
      required: false,
      visible: false,
    },
    parentMarginRateFloor: {
      inputType: 'text',
      type: 'number',
      title: 'Min Rate',
      required: false,
      visible: false,
    },
    parentMarginRateCeiling: {
      inputType: 'text',
      type: 'number',
      title: 'Max Rate',
      required: false,
      visible: false,
    },
    parentMarginPriceFloor: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price',
      required: false,
      visible: false,
    },
    parentMarginPriceCeiling: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price',
      required: false,
      visible: false,
    },
    parentPriceFloorRoundUp: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price Round Up',
      required: false,
      visible: false,
    },
    parentPriceCeilRoundUp: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price Round Up',
      required: false,
      visible: false,
    },
    parentPriceFloorRoundDown: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price Round Down',
      required: false,
      visible: false,
    },
    parentPriceCeilRoundDown: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price Round Down',
      required: false,
      visible: false,
    },

    marginSeperator1: {
      inputType: 'section',
      title: 'Margin Adjustment',
      span: 2,
      visible: false,
    },
    marginRate: {
      inputType: 'text',
      type: 'number',
      title: 'Rate Margin',
      required: false,
      visible: false,
    },
    marginPrice: {
      inputType: 'text',
      type: 'number',
      title: 'Price Margin',
      required: false,
      visible: false,
    },
    marginRateFloor: {
      inputType: 'text',
      type: 'number',
      title: 'Min Rate',
      required: false,
      visible: false,
    },
    marginRateCeiling: {
      inputType: 'text',
      type: 'number',
      title: 'Max Rate',
      required: false,
      visible: false,
    },
    marginPriceFloor: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price',
      required: false,
      visible: false,
    },
    marginPriceCeiling: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price',
      required: false,
      visible: false,
    },
    priceFloorRoundUp: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price Round Up',
      required: false,
      visible: false,
    },
    priceCeilRoundUp: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price Round Up',
      required: false,
      visible: false,
    },
    priceFloorRoundDown: {
      inputType: 'text',
      type: 'number',
      title: 'Min Price Round Down',
      required: false,
      visible: false,
    },
    priceCeilRoundDown: {
      inputType: 'text',
      type: 'number',
      title: 'Max Price Round Down',
      required: false,
      visible: false,
    },
    otherSeperator: {
      inputType: 'section',
      title: 'Others',
      span: 2,
      visible: false,
    },
    pullCreditScoreLevel: {
      inputType: 'radio',
      title: 'Pull Credit',
      options: {
        ' 1': 'Eligible to pull Credit Report',
        ' 0': 'Eligible to Pull Credit Report With Required Payment',
        ' -1': 'Not Eligible to pull Credit Report',
      },
      sort: false,
      required: false,
      visible: false,
      span: 2,
    },
    borrowerAutoNotify: {
      title: 'Borrower Auto Notification',
      inputType: 'check',
      error: '',
      required: false,
    },
    loanSharing: {
      title: 'Associated with Loan Sharing',
      inputType: 'check',
      error: '',
      required: false,
    },
    visibleLoansInOrg: {
      title: 'View Entire Organization Pipeline',
      inputType: 'check',
      error: '',
      required: false,
    },
    tfa: {
      title: 'Enable Multi-factor Authentication',
      inputType: 'check',
      error: '',
      required: true,
    },
    myLoanDefaultView: {
      title: '"My Loans" Default View',
      inputType: 'check',
      error: '',
      required: true,
    },
  }
}

export const userMarginInputs = [
  'marginSeperator1',
  'marginRate',
  'marginPrice',
  'marginRateFloor',
  'marginRateCeiling',
  'marginPriceFloor',
  'marginPriceCeiling',
  'priceFloorRoundUp',
  'priceCeilRoundUp',
  'priceFloorRoundDown',
  'priceCeilRoundDown',
]

export const userParentMarginInputs = [
  'parentMarginSeperator1',
  'parentMarginRate',
  'parentMarginPrice',
  'parentMarginRateFloor',
  'parentMarginRateCeiling',
  'parentMarginPriceFloor',
  'parentMarginPriceCeiling',
  'parentPriceFloorRoundUp',
  'parentPriceCeilRoundUp',
  'parentPriceFloorRoundDown',
  'parentPriceCeilRoundDown',
]
